/**
 * Class description
 *
 * Created: 2018-10-09
 * @author jonas
 */
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {ReelEvent} from "@nolimitcity/slot-game/bin/core/reel/event/ReelEvent";
import {IReelStoppedData} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {ReelStopPresentationController} from "@nolimitcity/slot-game/bin/core/reelstoppresentation/ReelStopPresentationController";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {TimelineLite} from "gsap";
import {DirtyWildGameModuleConfig} from "../DirtyWildGameModuleConfig";
import {DirtyWildsGameConfig} from "../DirtyWildsGameConfig";
import {DirtyWildsSoundConfig} from "../DirtyWildsSoundConfig";
import {DirtyWildsReelController} from "../reel/DirtyWildsReelController";
import {DirtyWildsParsedGameData, WildSymbolData} from "../server/data/DirtyWildsParsedGameData";
import {DirtyWildsStateReelSymbol} from "../symbols/DirtyWildsStateReelSymbol";
import {DirtyWildsRFSyncEvent} from "./DirtyWildsRFSyncController";

export class DirtyWildsRandomFeatureEvent {
    public static readonly INIT_SPEED_ACHIEVED:string = "dirtyWildsRandomFeatureEvent_initSpeedAchieved";
    public static readonly ACTIVATE_SLOW_SPIN:string = "dirtyWildsRandomFeatureEvent_activateSlowSpin";
    public static readonly RANDOM_QUICK_ANIMATE:string = "dirtyWildsRandomFeatureEvent_randomQuickAnimate";
}

export class DirtyWildsRandomFeatureReelStopController extends ReelStopPresentationController {
    private _isRandomFeatureTriggered:boolean;
    private _positionsWildInfo:WildSymbolData[];

    constructor(index:number, name:string) {
        super(index, name);
    }

    protected hasWin() {
        return this._isRandomFeatureTriggered;
    }

    protected parseFeatureGameData(data:DirtyWildsParsedGameData):void {
        this._isRandomFeatureTriggered = data.isRandomFeatureTriggered;
        this._positionsWildInfo = (<DirtyWildsParsedGameData>data).positionsWildInfo;
        if(this.hasWin()) {
            const tl:TimelineLite = new TimelineLite({
                onComplete : () => {
                    tl.kill();
                }
            });
            tl.add(() => this.startRandomPresentation());
            EventHandler.dispatchEvent(new GameEvent(SpinEvent.ADD_STOP_DELAY, 6));
        }
    }

    private startRandomPresentation():void {
        const tl:TimelineLite = new TimelineLite({
            onComplete : () => {
                DirtyWildGameModuleConfig.effectManager.getBackgroundZoomAnimation(2);
            }
        });
        const tlSpine:TimelineLite = DirtyWildGameModuleConfig.environment.startMysteryPresentation("TeaseStart", 0, 340, 370)!;
        const logoSpine:TimelineLite = DirtyWildGameModuleConfig.environment.startMysteryPresentation("FlashOnLogo", 0, 340, 370, 0)!;
        const endSpine:TimelineLite = DirtyWildGameModuleConfig.environment.startMysteryPresentation("TeaseToSmall", 0, 340, 370)!;
        tl.add([tlSpine, () => SlotGame.sound.playEffect(DirtyWildsSoundConfig.RADNOM_TEASE)], 0);
        tl.add([logoSpine,
                () => SlotGame.sound.playEffect(DirtyWildsSoundConfig.RANDOM_FEATURE_TRIGGERED),
                () => DirtyWildGameModuleConfig.soundPlayer.switchToFreespinMusic()],
            tlSpine.totalDuration() - 0.4);
        tl.add([endSpine,
            () => {EventHandler.dispatchEvent(new GameEvent(SpinEvent.STARTED))}]);
        tl.add([DirtyWildGameModuleConfig.environment.highlightFeature("mysteryWild"),
            () => SlotGame.sound.playEffect(DirtyWildsSoundConfig.RANDOM_FEATURE_LOCKED),
        ], tl.totalDuration() - (endSpine.totalDuration() / 1.6));
        tl.add(() => {
            EventHandler.dispatchEvent(new GameEvent(DirtyWildsRFSyncEvent.SET_EVENT_VARIABLE, true));
            EventHandler.dispatchEvent(new GameEvent(SpinEvent.STOP));
        }, tl.totalDuration() - (endSpine.totalDuration() / 1.6));
        tl.add(() => DirtyWildGameModuleConfig.environment.startMysteryPresentation("SmallLoop", -1, 340, 370));
        // tl.set({}, {}, (tlSpine.totalDuration() + logoSpine.totalDuration() + endSpine.totalDuration()));
        DirtyWildGameModuleConfig.effectManager.getTeaseIntense(3);
    }

    protected addFeatureEventHandlers():void {
        EventHandler.addEventListener(this, DirtyWildsRandomFeatureEvent.INIT_SPEED_ACHIEVED, (event:GameEvent) => this.initiateRandomBehaviour(event.params[0], event.params[1]));
        EventHandler.addEventListener(this, ReelEvent.ALL_REELS_SPIN_ANIMATION_STOPPED, (event:GameEvent) => this.onAllReelsStopped());
    }

    public onReelBounce(data:IReelStoppedData):void {
        if(data.reelId == 1 && this.hasWin()) {
            const reelController:DirtyWildsReelController = <DirtyWildsReelController>Reels.getReel(data.reelId)!;
            if(!reelController.isQuickStop()) {
                this.initiateRandomBehaviour(0.6, 25);
                SlotGame.sound.playEffect(DirtyWildsSoundConfig.RANDOM_FEATURE_SLOWDOWN);
            }
        }
    }

    private initiateRandomBehaviour(speed:number, steps:number):void {
        EventHandler.dispatchEvent(new GameEvent(DirtyWildsRandomFeatureEvent.ACTIVATE_SLOW_SPIN, speed, steps));
    }

    public onReelSpinStopped(data:IReelStoppedData):void {
        if(data.reelId == 2 && this.hasWin()) {
            SlotGame.sound.stopEffect(DirtyWildsSoundConfig.RANDOM_FEATURE_SLOWDOWN);
            let symToRemove:number[] = [0, 2];
            const reel:DirtyWildsReelController = <DirtyWildsReelController>Reels.getReel(2)!;
            let middleReelOverlays:WildSymbolData[] = this._positionsWildInfo.filter((a) => {return a.wildPosition[0] === 2 && a.wildPosition[1] !== 1});
            for(let i:number = 0; i < middleReelOverlays.length; i++) {
                let wildPosition:number = middleReelOverlays[i].wildPosition[1];
                if(symToRemove.indexOf(wildPosition) > -1) {
                    symToRemove.splice(symToRemove.indexOf(wildPosition), 1);
                }
            }
            for(let i:number = 0; i < symToRemove.length; i++) {
                const symbol:DirtyWildsStateReelSymbol = <DirtyWildsStateReelSymbol>Reels.getSymbol(2, symToRemove[i]);
                if(symbol) {
                    symbol.changeState({
                        state : DirtyWildsGameConfig.instance.SYMBOL_STATES.lowlight,
                        fadeInDuration : 0
                    });
                }
            }
            // SlotGame.sound.playEffect(DirtyWildsSoundConfig.RANDOM_FEATURE_ATTENTION);
            reel.deactivateSlowMotionSpin();

            //When REEL_ADD_STOP_DELAY is dispatched, that delay is added to all reels with id higher than de dispatched reelId.
            //But because reel 3 and 4 has allready stopped, the _addedStopDelay is not reset back to 0.
            //So that the delay stays until the next spin causing the last two reels to have a too long delay.
            //So this is here to prevent that.
            (<any>Reels.getReel(0))._addedStopDelay = 0;
            (<any>Reels.getReel(1))._addedStopDelay = 0;
            (<any>Reels.getReel(2))._addedStopDelay = 0;
            (<any>Reels.getReel(3))._addedStopDelay = 0;
            (<any>Reels.getReel(4))._addedStopDelay = 0;

            DirtyWildGameModuleConfig.environment.stopMysteryPresentation();
        }
        this.dispatchCompleteEvent(data.reelId);
    }

    private onAllReelsStopped():void {
        DirtyWildGameModuleConfig.environment.stopMysteryPresentation()
    }
}